/**
 * Network Status Hook
 * 
 * Monitors online/offline status using navigator.onLine and window events.
 * Also respects test mode simulation for development testing.
 * Use this to conditionally disable actions or show offline UI.
 */

import { useState, useEffect, useCallback } from 'react';
import { getSimulation, subscribeToSimulation } from '@/lib/testMode';

export interface NetworkStatus {
  isOnline: boolean;
  /** Time when status last changed */
  lastChanged: Date | null;
  /** Check if currently online (can be called imperatively) */
  checkOnline: () => boolean;
  /** Whether offline state is simulated */
  isSimulated: boolean;
}

export function useNetworkStatus(): NetworkStatus {
  const [isOnline, setIsOnline] = useState(() => 
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [lastChanged, setLastChanged] = useState<Date | null>(null);
  const [isSimulated, setIsSimulated] = useState(() => getSimulation() === 'offline');

  const checkOnline = useCallback(() => {
    // Test mode simulation takes priority
    if (getSimulation() === 'offline') return false;
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      // Only go online if not simulating offline
      if (getSimulation() !== 'offline') {
        setIsOnline(true);
        setLastChanged(new Date());
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setLastChanged(new Date());
    };

    // Check initial status
    if (!navigator.onLine || getSimulation() === 'offline') {
      setIsOnline(false);
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Subscribe to test mode changes
    const unsubscribe = subscribeToSimulation((mode) => {
      const simOffline = mode === 'offline';
      setIsSimulated(simOffline);
      if (simOffline) {
        setIsOnline(false);
        setLastChanged(new Date());
      } else {
        // Restore real network status
        setIsOnline(navigator.onLine);
        setLastChanged(new Date());
      }
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, []);

  // Combine real status with simulation
  const effectiveOnline = isSimulated ? false : isOnline;

  return { isOnline: effectiveOnline, lastChanged, checkOnline, isSimulated };
}

/**
 * Standalone check for network status (non-hook version)
 * Use when you need to check status outside of React components
 */
export function isNetworkOnline(): boolean {
  // Test mode simulation takes priority
  if (getSimulation() === 'offline') return false;
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}
